import { css, keyframes } from "@emotion/css";
import { CSSProperties } from "react";
import { mergeProps } from "react-aria";
import {
  Popover as AriaPopover,
  OverlayArrow,
  PopoverProps,
} from "react-aria-components";

import { Loader, useColors } from "@certn/cascadia-react";

interface Props extends PopoverProps {
  isLoading?: boolean;
  showArrow?: boolean;
  testID?: string;
  minWidth?: CSSProperties["minWidth"];
  maxWidth?: CSSProperties["maxWidth"];
}

export function _Popover(props: Props) {
  const colors = useColors();
  const { isLoading, showArrow, testID, ...popoverProps } = props;
  return (
    <AriaPopover
      {...mergeProps(popoverProps, {
        className: css([styles.popover]),
      })}
      style={{
        minWidth: props.minWidth || "var(--trigger-width)",
        maxWidth: props.maxWidth,
      }}
      data-testid={testID}
    >
      {(renderProps) => {
        const children =
          typeof props.children === "function"
            ? props.children(renderProps)
            : props.children;

        return (
          <>
            {showArrow && (
              <OverlayArrow>
                <svg
                  fill={colors["surface-overlay"]}
                  stroke={colors["border-base"]}
                  width={12}
                  height={12}
                  viewBox="0 0 12 12"
                >
                  <path d="M0 0 L6 6 L12 0" />
                </svg>
              </OverlayArrow>
            )}
            {children}
            {isLoading && <Loader overlay background="surface-overlay" />}
          </>
        );
      }}
    </AriaPopover>
  );
}

const slide = keyframes`
  from {
    transform: var(--origin);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const styles = {
  popover: css({
    backgroundColor: "var(--surface-overlay)",
    borderRadius: "var(--border-radius-2)",
    display: "flex",
    flexDirection: "column",
    boxShadow: "var(--shadow-menu)",

    ".react-aria-OverlayArrow svg": {
      display: "block",
      fill: "var(--surface-overlay)",
      stroke: "var(--border-base)",
      strokeWidth: "1px",
    },

    "&[data-placement=top]": {
      " --origin": "translateY(8px)",
      "&:has(.react-aria-OverlayArrow)": {
        marginBottom: "6px",
      },
    },

    "&[data-placement=bottom]": {
      "--origin": "translateY(-8px)",
      "&:has(.react-aria-OverlayArrow)": {
        marginTop: "6px",
      },

      ".react-aria-OverlayArrow svg": {
        transform: "rotate(180deg)",
      },
    },

    "&[data-placement=right]": {
      "--origin": "translateX(-8px)",

      "&:has(.react-aria-OverlayArrow)": {
        marginLeft: "6px",
      },

      ".react-aria-OverlayArrow svg": {
        transform: "rotate(90deg)",
      },
    },

    "&[data-placement=left]": {
      "--origin": " translateX(8px)",

      "&:has(.react-aria-OverlayArrow)": {
        marginRight: "6px",
      },

      ".react-aria-OverlayArrow svg": {
        transform: "rotate(-90deg)",
      },
    },

    "&[data-entering]": {
      animation: `${slide} 100ms`,
    },

    "&[data-exiting]": {
      animation: `${slide} 100ms reverse ease-in`,
    },

    ".react-aria-Dialog": {
      display: "flex",
      flexDirection: "column",
      outline: "none",
      position: "relative",
      borderRadius: "var(--border-radius-2)",
      overflowY: "auto",
      "&[data-focus-visible]": {
        boxShadow: "var(--focus-action)",
      },
    },
  }),
};
